import { handleAxiosError } from '@/api/helpers';
import { Account } from '@/client/accounts';
import { Actions, Subjects } from '@/client/users';
import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import { placeholderPreviewQueryKey, useUpdateAccounts } from '@/hooks/query';
import { useAppDispatch } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { setCurrentAccount } from '@/store/features/account';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import { formatDate } from '@/utils/date/date.helper';
import { AutoScheduleIcon } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { AutoScheduleModal } from './modals/AutoScheduleModal';

const StyledHeader = styled(FlexContainer)`
  padding: var(--medium-big-padding);
  border: 1px solid var(--gray-moderate);
  border-bottom: none;
  border-radius: var(--semi-medium-border-radius)
    var(--semi-medium-border-radius) 0 0;
  background: var(--white-main);
  gap: var(--xsmall-padding);

  .auto-schedule-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 43px;
    height: 43px;
    flex-shrink: 0;
    color: var(--green-color);

    svg {
      width: 16px;
      height: 26px;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxxsmall-padding);
  flex: 1;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xxxsmall-padding);
  font-size: var(--small-font-size);
  font-weight: 600;
  color: var(--black-main);
`;

const Description = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: 400;
  color: var(--black-main);
  line-height: 1.5;
`;

const StyledConfirmDialog = styled(Dialog)`
  width: 500px;
  max-width: 90vw;

  .p-dialog-header {
    padding: var(--semi-big-padding) var(--semi-big-padding) 0;
    font-size: var(--modal-heading-font-size);
    font-weight: 700;
    line-height: 1.4;
    color: var(--black-main);
  }

  .p-dialog-content {
    padding: 0 var(--semi-big-padding) var(--semi-big-padding);
  }
`;

const ConfirmMessage = styled.p`
  font-size: var(--small-font-size);
  line-height: 1.5;
  color: var(--black-main);
  margin: 0 0 var(--default-padding);
`;

export type AutoScheduleOperation = 'enable' | 'disable';

type AutoScheduleHeaderProps = {
  currentAccount?: Account;
  isDemoAccount?: boolean;
  onLoadingChange?: (
    loading: boolean,
    operation: AutoScheduleOperation,
  ) => void;
};

export const AutoScheduleHeader: React.FC<AutoScheduleHeaderProps> = ({
  currentAccount,
  isDemoAccount,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const { isRestrictedByRole } = usePermission();
  const isToggleRestricted = isRestrictedByRole(
    Actions.UPDATE,
    Subjects.AUTO_PHISHING_CAMPAIGNS,
  );
  const toast = useToast();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const updateAccountMutation = useUpdateAccounts();
  const isEnabled = !!currentAccount?.enableAutoPhishingMode;
  const isToggleDisabled =
    !currentAccount?.isPhishingSenderWhitelisted || isDemoAccount;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmDisableOpen, setIsConfirmDisableOpen] = useState(false);
  const [operation, setOperation] = useState<AutoScheduleOperation>('enable');

  useEffect(() => {
    onLoadingChange?.(updateAccountMutation.isLoading, operation);
  }, [updateAccountMutation.isLoading, operation]);

  const handleToggle = (enabled: boolean) => {
    if (enabled) {
      setIsModalOpen(true);
      return;
    }

    setIsConfirmDisableOpen(true);
  };

  const handleModalConfirm = async (
    simulationsPerYear: number,
    startDate: Date,
  ) => {
    const accountId = currentAccount?.id;
    if (!accountId) return;

    setOperation('enable');

    try {
      await updateAccountMutation.update({
        accountId,
        updates: {
          enableAutoPhishingMode: true,
          autoPhishingSimulationsPerYear: simulationsPerYear,
          autoPhishingStartDate:
            formatDate(startDate, DateFormats.DATEONLY_DASHED) ?? undefined,
        },
      });
      dispatch(
        setCurrentAccount({
          ...currentAccount,
          enableAutoPhishingMode: true,
          autoPhishingSimulationsPerYear: simulationsPerYear,
        }),
      );
      queryClient.invalidateQueries(['campaigns']);
      queryClient.invalidateQueries(['accounts']);
      queryClient.invalidateQueries([placeholderPreviewQueryKey, accountId]);
      toast?.success(
        t('toast.success'),
        t('phishing.autoSchedule.enabled.success', {
          date: formatDate(startDate, DateFormats.DATEONLY_FULL_MONTH),
        }),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const handleConfirmDisable = async () => {
    setIsConfirmDisableOpen(false);
    const accountId = currentAccount?.id;
    if (!accountId) return;

    setOperation('disable');

    try {
      await updateAccountMutation.update({
        accountId,
        updates: { enableAutoPhishingMode: false },
      });
      dispatch(
        setCurrentAccount({
          ...currentAccount,
          enableAutoPhishingMode: false,
        }),
      );
      queryClient.invalidateQueries(['campaigns']);
      queryClient.invalidateQueries(['accounts']);
      queryClient.invalidateQueries([placeholderPreviewQueryKey, accountId]);
      toast?.success(
        t('toast.success'),
        t('phishing.autoSchedule.disabled.success'),
      );
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <>
      <StyledHeader justify="flex-start" align="center">
        <div className="auto-schedule-icon">
          <AutoScheduleIcon />
        </div>
        <Content>
          <Title>
            {t('phishing.simulations.autoSchedule.title')}
            <AppIconTooltip
              target="auto-schedule-info"
              content={t('phishing.simulations.autoSchedule.tooltip')}
            />
          </Title>
          <Description>
            {t('phishing.simulations.autoSchedule.description')}
          </Description>
        </Content>
        <ViewOnlyRestrictionWhen restricted={isToggleRestricted}>
          <div id="auto-schedule-toggle">
            <AppSwitch
              className="v2"
              checked={isEnabled}
              disabled={
                isToggleDisabled ||
                isToggleRestricted ||
                updateAccountMutation.isLoading
              }
              onChange={(e) =>
                !isDemoAccount && !isToggleRestricted && handleToggle(e.value)
              }
            />
          </div>
        </ViewOnlyRestrictionWhen>
        {!currentAccount?.isPhishingSenderWhitelisted && (
          <Tooltip
            target="#auto-schedule-toggle"
            content={t(
              'phishing.simulations.autoSchedule.whitelistingRequired',
            )}
            position="left"
          />
        )}
      </StyledHeader>

      <AutoScheduleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleModalConfirm}
      />

      <StyledConfirmDialog
        visible={isConfirmDisableOpen}
        onHide={() => setIsConfirmDisableOpen(false)}
        modal
        draggable={false}
        resizable={false}
        className="v2"
        header={t('phishing.autoSchedule.disable.title')}
      >
        <ConfirmMessage>
          {t('phishing.autoSchedule.disable.message')}
        </ConfirmMessage>

        <FlexContainer justify="flex-start" gap="var(--xxsmall-padding)">
          <AppButton
            label={t('generic.confirm')}
            severity="danger"
            onClick={handleConfirmDisable}
          />
          <AppButton
            label={t('generic.cancel')}
            severity="secondary"
            type="outlined"
            onClick={() => setIsConfirmDisableOpen(false)}
          />
        </FlexContainer>
      </StyledConfirmDialog>
    </>
  );
};
